import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import type { Device, TrackPoint, Trip, TripSummary } from '../api/types';
import { MapView, SPEED_BANDS, type BaseMap } from '../components/MapView';
import { IconLayers, IconPause, IconPlay, IconRoute } from '../components/Icons';
import { useToast } from '../components/Toast';
import { formatDateTime, formatDuration, formatTime, useT } from '../i18n';

const DAY_MS = 24 * 3600 * 1000;
const PLAYBACK_SPEEDS = [1, 2, 4, 8, 16, 32, 64];
/** Point budget for the whole requested window. */
const MAX_POINTS = 6000;

interface Props {
  devices: Device[];
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/** Value for a datetime-local input, which wants local time with no zone. */
function toInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

type Preset = 'today' | 'yesterday' | 'last7' | 'last30' | null;

/** A point on the playback timeline, positioned by driving time rather than clock time. */
interface TimelinePoint extends TrackPoint {
  offsetMs: number;
}

/**
 * Lay trips end to end on a single timeline.
 *
 * Playback uses driving time, not wall-clock time: a month contains hundreds of
 * parked hours, and scrubbing through those would be watching nothing happen.
 * Skipping the gaps means the whole month plays in minutes.
 */
function buildTimeline(trips: Trip[]): TimelinePoint[] {
  const timeline: TimelinePoint[] = [];
  let accumulated = 0;

  for (const trip of trips) {
    const points = trip.points ?? [];
    if (points.length === 0) continue;

    const tripStart = Date.parse(points[0]!.deviceTime);
    for (const point of points) {
      timeline.push({ ...point, offsetMs: accumulated + (Date.parse(point.deviceTime) - tripStart) });
    }
    accumulated += Date.parse(points[points.length - 1]!.deviceTime) - tripStart;
  }

  return timeline;
}

function SpeedLegend() {
  const { t } = useT();
  return (
    <div className="speed-legend">
      <span className="legend-title">{t('speed')}</span>
      {SPEED_BANDS.map((band, i) => {
        const lower = i === 0 ? 0 : SPEED_BANDS[i - 1]!.upTo;
        return (
          <span key={i} className="legend-item">
            <i style={{ background: band.colour }} />
            {Number.isFinite(band.upTo) ? `${lower}-${band.upTo}` : `${lower}+`}
          </span>
        );
      })}
      <span className="legend-unit">km/h</span>
    </div>
  );
}

export function Replay({ devices }: Props) {
  const { t, lang, rtl } = useT();
  const toast = useToast();

  const [deviceId, setDeviceId] = useState<number | null>(devices[0]?.id ?? null);
  const [from, setFrom] = useState(() => startOfDay(new Date()));
  const [to, setTo] = useState(() => new Date());
  const [preset, setPreset] = useState<Preset>('today');

  const [trips, setTrips] = useState<Trip[]>([]);
  const [summary, setSummary] = useState<TripSummary | null>(null);
  const [totalFixes, setTotalFixes] = useState(0);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [wholeRange, setWholeRange] = useState(false);
  /** Fixes to draw when the window contains no trips at all. */
  const [parked, setParked] = useState<TrackPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [fitKey, setFitKey] = useState(0);
  const [basemap, setBasemap] = useState<BaseMap>('clean');

  const [playing, setPlaying] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [multiplier, setMultiplier] = useState(8);
  const frameRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number>(0);

  // Pick the first device once the list arrives.
  useEffect(() => {
    if (deviceId === null && devices.length > 0) setDeviceId(devices[0]!.id);
  }, [devices, deviceId]);

  const load = async (announce = true) => {
    if (deviceId === null) return;
    setLoading(true);
    setSelectedTrip(null);
    setWholeRange(false);
    setPlaying(false);
    setElapsedMs(0);

    try {
      const result = await api.trips(deviceId, from, to, true, MAX_POINTS);
      setTrips(result.trips);
      setSummary(result.summary);
      setTotalFixes(result.totalCount);
      setFitKey((key) => key + 1);

      if (result.trips.length === 0) {
        // Only when the customer asked. An automatic load announcing "no trips"
        // every time the page opens is noise; the empty state on the map says
        // the same thing without interrupting.
        if (announce) toast('info', t('noTrips'));
      } else {
        // Show the whole window, always. Previously only ranges longer than two
        // days did, so opening Replay on "today" loaded eight trips and drew
        // nothing at all until one was clicked -- which reads as a platform
        // with no history rather than a map waiting for a selection.
        setWholeRange(true);
      }

      // A vehicle that was parked all day has fixes but no trips, and an empty
      // map is a poor way to say "it did not move". Draw where it sat.
      if (result.trips.length === 0 && result.totalCount > 0) {
        const track = await api.track(deviceId, from, to, MAX_POINTS);
        setParked(track.points);
      } else {
        setParked([]);
      }
    } catch (cause) {
      toast('err', (cause as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // The whole point of opening Replay is to see the history. Waiting for a
  // button press to fetch anything made the page look like it had no data at
  // all, which is exactly how it was reported.
  //
  // Keyed on the timestamps rather than the Date objects: `new Date()` is a new
  // identity every render, and depending on the objects would refetch forever.
  useEffect(() => {
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, from.getTime(), to.getTime()]);

  const applyPreset = (next: Exclude<Preset, null>) => {
    const now = new Date();
    setPreset(next);
    if (next === 'today') {
      setFrom(startOfDay(now));
      setTo(now);
    } else if (next === 'yesterday') {
      const start = startOfDay(new Date(now.getTime() - DAY_MS));
      setFrom(start);
      setTo(new Date(start.getTime() + DAY_MS - 1));
    } else if (next === 'last7') {
      setFrom(startOfDay(new Date(now.getTime() - 7 * DAY_MS)));
      setTo(now);
    } else {
      setFrom(startOfDay(new Date(now.getTime() - 30 * DAY_MS)));
      setTo(now);
    }
  };

  const timeline = useMemo(() => {
    // A selected trip always wins: clicking one is an explicit request to look
    // at it rather than at everything.
    if (selectedTrip) return buildTimeline([selectedTrip]);
    if (trips.length > 0) return buildTimeline(trips);
    // No trips, but the vehicle reported from somewhere. One synthetic segment
    // so the map has a position to show and fit to.
    return parked.map((point, index) => ({ ...point, offsetMs: index * 1000 }));
  }, [trips, selectedTrip, parked]);

  const totalMs = timeline.length > 1 ? timeline[timeline.length - 1]!.offsetMs : 0;

  // Drive playback from wall-clock time so the multiplier is honest and the
  // animation stays smooth regardless of how far apart the fixes are.
  useEffect(() => {
    if (!playing || totalMs === 0) return;

    lastFrameRef.current = performance.now();
    const step = (now: number) => {
      const delta = now - lastFrameRef.current;
      lastFrameRef.current = now;

      setElapsedMs((current) => {
        const next = current + delta * multiplier;
        if (next >= totalMs) {
          setPlaying(false);
          return totalMs;
        }
        return next;
      });
      frameRef.current = requestAnimationFrame(step);
    };

    frameRef.current = requestAnimationFrame(step);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [playing, multiplier, totalMs]);

  /** Interpolate the vehicle between the two fixes surrounding the play cursor. */
  const playhead = useMemo(() => {
    if (timeline.length === 0) return null;

    // Binary search: a month can be thousands of points and this runs per frame.
    let low = 0;
    let high = timeline.length - 1;
    while (low < high - 1) {
      const mid = (low + high) >> 1;
      if (timeline[mid]!.offsetMs <= elapsedMs) low = mid;
      else high = mid;
    }

    const current = timeline[low]!;
    const next = timeline[Math.min(low + 1, timeline.length - 1)]!;
    const span = next.offsetMs - current.offsetMs;
    const ratio = span > 0 ? Math.min(1, Math.max(0, (elapsedMs - current.offsetMs) / span)) : 0;

    return {
      latitude: current.latitude + (next.latitude - current.latitude) * ratio,
      longitude: current.longitude + (next.longitude - current.longitude) * ratio,
      course: current.course,
      speedKph: current.speedKph,
      deviceTime: current.deviceTime,
    };
  }, [timeline, elapsedMs]);

  const openTrip = (trip: Trip) => {
    setSelectedTrip(trip);
    setWholeRange(false);
    setElapsedMs(0);
    setPlaying(false);
    setFitKey((key) => key + 1);
  };

  const showWholeRange = () => {
    setWholeRange(true);
    setSelectedTrip(null);
    setElapsedMs(0);
    setPlaying(false);
    setFitKey((key) => key + 1);
  };

  const progress = totalMs > 0 ? (elapsedMs / totalMs) * 100 : 0;
  const thinned = totalFixes > timeline.length && timeline.length > 0;

  return (
    <div className="replay-layout page">
      <section className="replay-sidebar">
        <label>
          {t('vehicles')}
          <select
            value={deviceId ?? ''}
            onChange={(event) => setDeviceId(Number(event.target.value))}
          >
            {devices.map((device) => (
              <option key={device.id} value={device.id}>
                {device.name}
              </option>
            ))}
          </select>
        </label>

        <div className="preset-row">
          {(
            [
              ['today', t('today')],
              ['yesterday', t('yesterday')],
              ['last7', t('last7')],
              ['last30', t('last30')],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={preset === key ? 'chip active' : 'chip'}
              onClick={() => applyPreset(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <label>
          {t('from')}
          <input
            type="datetime-local"
            value={toInputValue(from)}
            onChange={(event) => {
              setPreset(null);
              setFrom(new Date(event.target.value));
            }}
          />
        </label>
        <label>
          {t('to')}
          <input
            type="datetime-local"
            value={toInputValue(to)}
            onChange={(event) => {
              setPreset(null);
              setTo(new Date(event.target.value));
            }}
          />
        </label>

        <button
          type="button"
          className="btn primary block"
          onClick={() => load(true)}
          disabled={loading}
        >
          {loading ? <span className="btn-spinner" /> : <IconRoute />}
          {loading ? t('loading') : t('load')}
        </button>

        {summary && (
          <div className="summary-cards">
            <div className="summary-card">
              <div className="label">{t('totalDistance')}</div>
              <div className="value">
                {summary.distanceKm} <span style={{ fontSize: 12 }}>km</span>
              </div>
            </div>
            <div className="summary-card">
              <div className="label">{t('maxSpeed')}</div>
              <div className="value">
                {summary.maxSpeedKph} <span style={{ fontSize: 12 }}>km/h</span>
              </div>
            </div>
            <div className="summary-card">
              <div className="label">{t('movingTime')}</div>
              <div className="value">{formatDuration(summary.movingSeconds, lang)}</div>
            </div>
            <div className="summary-card">
              <div className="label">{t('idleTime')}</div>
              <div className="value">{formatDuration(summary.idleSeconds, lang)}</div>
            </div>
          </div>
        )}

        {trips.length > 0 && (
          <button
            type="button"
            className={wholeRange ? 'btn primary block' : 'btn block'}
            onClick={showWholeRange}
          >
            <IconRoute />
            {t('wholePeriod')}
          </button>
        )}

        <span className="section-label">
          {t('trips')} {trips.length > 0 && <span className="count">{trips.length}</span>}
        </span>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[0, 1, 2].map((i) => (
              <div key={i} className="skeleton" style={{ height: 48 }} />
            ))}
          </div>
        ) : trips.length === 0 ? (
          <p className="muted small">
            {totalFixes > 0 ? `${t('noTripsButParked')} (${totalFixes})` : t('noTrips')}
          </p>
        ) : (
          <ul className="trip-list">
            {trips.map((trip, i) => (
              <li key={trip.index}>
                <button
                  type="button"
                  className={selectedTrip?.index === trip.index ? 'trip-row selected' : 'trip-row'}
                  style={
                    {
                      animationDelay: `${Math.min(i, 12) * 24}ms`,
                      '--nudge': rtl ? -1 : 1,
                    } as React.CSSProperties
                  }
                  onClick={() => openTrip(trip)}
                >
                  <span className="trip-index">{trip.index + 1}</span>
                  <span className="trip-body">
                    <strong>
                      {formatTime(trip.startTime, lang)} → {formatTime(trip.endTime, lang)}
                    </strong>
                    <small>
                      {trip.distanceKm} km · {formatDuration(trip.durationSeconds, lang)} ·{' '}
                      {trip.maxSpeedKph} km/h
                    </small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {thinned && (
          <p className="muted tiny">
            {t('showingFixes')
              .replace('{shown}', timeline.length.toLocaleString())
              .replace('{total}', totalFixes.toLocaleString())}
          </p>
        )}
      </section>

      <div className="replay-main">
        <MapView track={timeline} playhead={playhead} fitKey={fitKey} basemap={basemap} />

        <div className="map-controls">
          <div className="map-control-group">
            <button
              type="button"
              className={basemap === 'clean' ? 'map-btn active' : 'map-btn'}
              onClick={() => setBasemap('clean')}
            >
              <IconLayers />
              {t('mapClean')}
            </button>
            <button
              type="button"
              className={basemap === 'streets' ? 'map-btn active' : 'map-btn'}
              onClick={() => setBasemap('streets')}
            >
              {t('mapStreets')}
            </button>
            <button
              type="button"
              className={basemap === 'satellite' ? 'map-btn active' : 'map-btn'}
              onClick={() => setBasemap('satellite')}
            >
              {t('mapSatellite')}
            </button>
            <button
              type="button"
              className={basemap === 'night' ? 'map-btn active' : 'map-btn'}
              onClick={() => setBasemap('night')}
            >
              {t('mapNight')}
            </button>
          </div>
        </div>

        {timeline.length > 1 && <SpeedLegend />}

        {timeline.length > 1 && (
          <div className="playback-bar">
            <button
              type="button"
              className="play-btn"
              aria-label={playing ? t('pause') : t('play')}
              onClick={() => {
                // Pressing play at the end means "watch it again".
                if (elapsedMs >= totalMs) setElapsedMs(0);
                setPlaying(!playing);
              }}
            >
              {playing ? <IconPause /> : <IconPlay />}
            </button>

            <div className="scrub">
              <div className="scrub-track">
                <div className="scrub-fill" style={{ width: `${progress}%` }} />
              </div>
              <input
                type="range"
                min={0}
                max={totalMs}
                value={elapsedMs}
                aria-label={t('replay')}
                onChange={(event) => {
                  setPlaying(false);
                  setElapsedMs(Number(event.target.value));
                }}
              />
            </div>

            <div className="playback-readout">
              <span>
                <b>{Math.round(playhead?.speedKph ?? 0)}</b> km/h
              </span>
              <span className="muted">{Math.round(playhead?.course ?? 0)}°</span>
              <span className="muted">
                {playhead ? formatDateTime(playhead.deviceTime, lang) : ''}
              </span>
            </div>

            <select
              value={multiplier}
              onChange={(e) => setMultiplier(Number(e.target.value))}
              aria-label={t('playbackSpeed')}
              style={{ width: 'auto' }}
            >
              {PLAYBACK_SPEEDS.map((value) => (
                <option key={value} value={value}>
                  {value}x
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
